/**
 * Quick View Controller
 *
 * Custom element that manages quick view functionality for products.
 * Uses global event delegation to handle all quick view button clicks.
 * Loads product HTML and initializes Splide carousel.
 *
 * Architecture:
 * - Custom element wrapper for lifecycle management
 * - Global event delegation for all quick-view buttons
 * - Fetches product HTML from Shopify
 * - Emits events for Alpine modal to handle open/close
 *
 * Features:
 * - Quick view button click handling
 * - Product HTML fetching from Shopify view
 * - Button state management (loading, idle, error)
 * - Automatic variant synchronization
 * - Splide carousel initialization
 * - Product handle extraction from links
 *
 * Events Emitted:
 * - quickview:open { productHandle, variantId } - After product loads successfully
 *
 * Data Attributes Required:
 * - [data-quick-view] - Quick view button (required)
 * - [data-btn-type="icon"] - Optional: prevents text content updates for icon-only buttons
 * - [data-quick-view-content] - Container element where product HTML is injected (in modal)
 *
 * Button States:
 * - data-state="idle" - Default state
 * - data-state="loading" - While fetching product
 * - data-state="error" - If fetch fails (reverts to idle after 2s)
 *
 * Workflow:
 * 1. User clicks [data-quick-view] button
 * 2. Controller extracts variant ID from product-controller's input[name="id"]
 * 3. Controller extracts product handle from product link href
 * 4. Controller fetches /products/{handle}?view=quick-view&variant={id}
 * 5. Controller injects HTML into [data-quick-view-content]
 * 6. Controller initializes Splide carousel
 * 7. Controller emits quickview:open event
 * 8. Alpine modal listens and opens
 *
 * Dependencies:
 * - Splide.js for image carousel
 * - Alpine.js for modal management
 * - product-controller for variant ID
 *
 * Locale Strings Used:
 * - window.themeStrings.quick_view.loading - "Loading..." button text
 * - window.themeStrings.quick_view.select_options - "Select Options" default text
 * - window.themeStrings.quick_view.error - "Error Loading" error text
 *
 * Usage:
 * Place <quick-view-controller></quick-view-controller> in your theme layout.
 * All quick-view buttons will be automatically handled via event delegation.
 */

class QuickViewController extends HTMLElement {
  constructor() {
    super();

    this.contentEl = null;
    this.splide = null;

    // Save bound references for proper cleanup
    this._boundHandleClick = this._handleDelegatedClick.bind(this);
  }

  connectedCallback() {
    this.contentEl = document.querySelector("[data-quick-view-content]");

    // Event delegation
    document.addEventListener("click", this._boundHandleClick);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this._boundHandleClick);
  }

  /* ===========================================
   * EVENT DELEGATION
   * =========================================== */

  _handleDelegatedClick(e) {
    const target = e.target.closest("[data-quick-view]");
    if (!target) return;

    e.preventDefault();
    this._handleQuickView(target);
  }

  /* ===========================================
   * QUICK VIEW HANDLING
   * =========================================== */

  async _handleQuickView(button) {
    const productController = button.closest("product-controller");
    if (!productController) {
      console.warn("Quick view button not inside product-controller");
      return;
    }

    const variantInput = productController.querySelector('input[name="id"]');
    const variantId = variantInput?.value;

    if (!variantId) {
      console.warn("No variant selected");
      return;
    }

    // Get product handle from product link
    const anchor = productController.querySelector('a[href*="/products/"]');
    let productHandle = null;

    if (anchor) {
      const url = new URL(anchor.href, window.location.origin);
      productHandle = url.pathname
        .replace(/^\/products\//, "")
        .replace(/\/.*$/, "");
    }

    if (!productHandle) {
      console.error("Quick View: Product handle not found");
      return;
    }

    this._updateButtonState(productController, "loading");

    try {
      await this._loadProduct(productHandle, variantId);

      this._updateButtonState(productController, "idle");

      // Emit event to open modal
      Shopify.emitEvent("quickview:open", {
        productHandle,
        variantId,
      });
    } catch (error) {
      console.error("Quick View Load Error:", error);

      this._updateButtonState(productController, "error");

      setTimeout(() => {
        this._updateButtonState(productController, "idle");
      }, 2000);
    }
  }

  /* ===========================================
   * PRODUCT LOADING
   * =========================================== */

  async _loadProduct(handle, variantId) {
    if (!this.contentEl) return;

    // Build URL with variant parameter if provided
    let url = `/products/${handle}?view=quick-view`;
    if (variantId) {
      url += `&variant=${variantId}`;
    }

    const res = await fetch(url);
    if (!res.ok) throw new Error("Failed to load product");

    const html = await res.text();
    this.contentEl.innerHTML = html;

    // Initialize carousel after DOM update
    await new Promise((resolve) => setTimeout(resolve, 0));

    this._initSplide();
  }

  /* ===========================================
   * BUTTON STATE MANAGEMENT
   * =========================================== */

  _updateButtonState(productController, state) {
    productController.querySelectorAll("[data-quick-view]").forEach((btn) => {
      btn.dataset.state = state;

      switch (state) {
        case "loading":
          btn.disabled = true;
          if (btn.dataset.btnType !== "icon") {
            btn.textContent =
              window.themeStrings?.quick_view?.loading || "Loading...";
          }
          break;

        case "idle":
          btn.disabled = false;
          if (btn.dataset.btnType !== "icon") {
            btn.textContent =
              window.themeStrings?.quick_view?.select_options ||
              "Select Options";
          }
          break;

        case "error":
          btn.disabled = false;
          if (btn.dataset.btnType !== "icon") {
            btn.textContent =
              window.themeStrings?.quick_view?.error || "Error Loading";
          }
          break;
      }
    });
  }

  /* ===========================================
   * SPLIDE CAROUSEL
   * =========================================== */

  _initSplide() {
    const splideRoot = this.contentEl?.querySelector(".splide");
    if (!splideRoot || !window.Splide) return;

    const viewport = splideRoot.querySelector(".splide__track");
    if (!viewport) return;

    this.splide = new Splide(splideRoot, {
      type: "fade",
      pagination: false,
      arrows: false,
    }).mount();

    this._setupSplideArrowButtons();
  }

  _setupSplideArrowButtons() {
    if (!this.splide || !this.contentEl) return;

    const prevBtn = this.contentEl.querySelector(".slide-prev-btn");
    const nextBtn = this.contentEl.querySelector(".slide-next-btn");

    if (!prevBtn || !nextBtn) return;

    const updateButtons = () => {
      const totalSlides = this.splide.Components.Elements.slides.length;
      const currentIndex = this.splide.index;

      prevBtn.disabled = currentIndex === 0;
      nextBtn.disabled = currentIndex >= totalSlides - 1;
    };

    updateButtons();
    this.splide.on("move", updateButtons);

    prevBtn.addEventListener("click", () => this.splide.go("<"));
    nextBtn.addEventListener("click", () => this.splide.go(">"));
  }
}

customElements.define("quick-view-controller", QuickViewController);
